import clsx from 'clsx';
import React, { useCallback, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import { root_path, subscribe_promotion_path } from '../../../routes';
import { PROFILE_MODAL_OFFER_CODE } from '../../../src/constants';
import type { FormikSubmitForm } from '../../../utils/types';
import { TIModal } from '../../BaseUI';
import StepBasicInfo from './StepBasicInfo';
import StepComplete from './StepComplete';
import StepEnableProfile from './StepEnableProfile';

const queryClient = new QueryClient();

export enum ModalHeading {
  ForumComment = 'Enable your profile to post to the forum',
  ArticleComment = 'Enable your profile to comment on this article',
  Subscriber = 'Enable your profile to connect with other subscribers',
  Lead = 'Subscribe to The Information',
}

export enum ModalSubheading {
  Comment = 'Join this discussion',
  Subscriber = 'Join the community',
  DirectoryLead = "Become a subscriber to view The Information's exclusive subscriber directory",
  ForumPostLead = 'Become a subscriber to publish a post',
}

export enum ModalLinkText {
  ForumComment = 'Enable Profile & Post to Forum',
  ArticleComment = 'Enable Profile & Post Comment',
  Subscriber = 'Turn On My Profile',
  Lead = 'Subscribe & Save 25%',
}

export enum ModalLinkUrl {
  Lead = subscribe_promotion_path({ offer: PROFILE_MODAL_OFFER_CODE }),
}

export type ModalContent = {
  heading: ModalHeading;
  subheading: ModalSubheading;
  linkText: ModalLinkText;
  linkUrl?: ModalLinkUrl;
};

export type CommentData = {
  type: string;
  id: number;
  page_to_reload: string;
};

type EnableProfileModalProps = {
  isLoggedIn: boolean;
  isSubscriber: boolean;
  isTriggeredByComment?: boolean;
  commentButtonText?: string;
  isCommentForForum?: boolean;
  handleSubmitComment?: FormikSubmitForm;
  commentData?: CommentData;
  buttonClasses?: string;
  modalContent?: ModalContent;
};

const EnableProfileModal = ({
  isLoggedIn,
  isSubscriber,
  isTriggeredByComment,
  commentButtonText,
  isCommentForForum,
  handleSubmitComment,
  commentData, // TODO: remove when we render all comments from react components
  buttonClasses,
  modalContent,
}: EnableProfileModalProps) => {
  // don't need this state (can pass from parents) once this component is always rendered by react components
  const [isOpen, setIsOpen] = useState(!isTriggeredByComment);
  const [isSaving, setIsSaving] = useState(false);
  const [step, setStep] = useState(0);
  const [profileEnabled, setProfileEnabled] = useState(false);
  const triggerCommentRef = useRef();

  const onClose = useCallback(() => {
    // if user is commenting refresh page when modal closes
    // TODO don't refresh page when article comments are rendered from react
    const refreshOnClose = isTriggeredByComment && !isCommentForForum && profileEnabled;
    if (refreshOnClose) {
      const searchParams = new URLSearchParams(window.location.search);
      const currPage = searchParams.get('page');
      const reloadSamePage = currPage == commentData['page_to_reload'];
      if (!reloadSamePage) {
        searchParams.set('page', commentData['page_to_reload']);
        const newRelativePath = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.replaceState(null, '', newRelativePath);
      }

      window.location.reload();
    }
    const redirectOnClose = !isTriggeredByComment && !profileEnabled;
    return redirectOnClose ? (window.location = root_path()) : setIsOpen(false);
  }, [isTriggeredByComment, profileEnabled, isCommentForForum, commentData]);

  const currentStep = () => {
    switch (step) {
      case 0:
        return (
          <StepEnableProfile
            nextStep={() => setStep(1)}
            isLoggedIn={isLoggedIn}
            isSaving={isSaving}
            setIsSaving={setIsSaving}
            setProfileEnabled={setProfileEnabled}
            isTriggeredByComment={isTriggeredByComment}
            handleSubmitComment={handleSubmitComment}
            commentData={commentData} // TODO: remove once article comments render from react
            modalContent={
              modalContent ||
              (isSubscriber
                ? {
                    heading: isTriggeredByComment
                      ? isCommentForForum
                        ? ModalHeading.ForumComment
                        : ModalHeading.ArticleComment
                      : ModalHeading.Subscriber,
                    subheading: isTriggeredByComment
                      ? ModalSubheading.Comment
                      : ModalSubheading.Subscriber,
                    linkText: isTriggeredByComment
                      ? isCommentForForum
                        ? ModalLinkText.ForumComment
                        : ModalLinkText.ArticleComment
                      : ModalLinkText.Subscriber,
                  }
                : {
                    heading: ModalHeading.Lead,
                    subheading: ModalSubheading.DirectoryLead,
                    linkText: ModalLinkText.Lead,
                    linkUrl: ModalLinkUrl.Lead,
                  })
            }
            getCurrentTriggerComment={() => triggerCommentRef.current}
          />
        );
      case 1:
        return <StepBasicInfo nextStep={() => setStep(2)} />;
      case 2:
        return <StepComplete nextStep={onClose} />;
      default:
        onClose();
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      {isTriggeredByComment && (
        <button
          ref={triggerCommentRef}
          className={clsx('button button-submit button-small', buttonClasses)}
          type="button"
          disabled={isSaving}
          onClick={(e) => {
            if (!profileEnabled) {
              e.preventDefault();
              setIsOpen(true);
            }
          }}
        >
          {commentButtonText}
        </button>
      )}
      <TIModal
        isOpen={isOpen}
        parentClass="EnableProfileModal"
        disableScrollLock={false}
        onClose={onClose}
      >
        {currentStep()}
      </TIModal>
    </QueryClientProvider>
  );
};

export default EnableProfileModal;
